// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';
import moment from 'moment';
import Switch from 'react-switch';

import Select2, { DataSelect2, EventSelect2 } from 'wdna-select2';
import DatePicker from 'wdna-date-selector';

import { UserDO } from 'wdna-components';
import UiValidate from 'wdna-components/lib/forms/validation/UiValidate';
import { initProcess, finishProcess } from 'wdna-components/lib/scheduler/actions';
import { messageError, messageWarning } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';

import { ContextType, FilterContext } from '../../../../../types';
import { typeSensorVariable, TypeSensorVariableListDO } from '../../../../configuration/requests';
import { sensor, SensorDO, SensorListDO, weather } from '../../../../sensors/requests';
import Report from './Report';

// ID 4 is the weather station type sensor
const TYPE_SENSOR_ID: number = 4;

interface Props {
  rowId?: number;
}

interface State {
  sensorArray: SensorListDO[];
  typeVariableArray: TypeSensorVariableListDO[];

  date: Date[];
  flatpickrMode: string;
  groupDateTime: string;

  dateIni: string;
  timeIni: string;
  dateEnd: string;
  timeEnd: string;
  groupTime: string;

  variables: any[];
  modalShow: boolean;
  autoRefresh: boolean;

  id?: number;
}

export default class Graph extends React.Component<Props, State> {
  private id: string | number | undefined;
  private pid: string;
  private mounted: boolean;

  private timer: NodeJS.Timer | undefined;

  public static contextType: React.Context<ContextType> = FilterContext;
  public context: React.ContextType<typeof FilterContext> | undefined;

  public constructor(props: Props) {
    super(props);

    this.pid = window.getStr();
    this.mounted = false;

    const dIni: Date = new Date();
    dIni.setDate(dIni.getDate() - 2);
    const dEnd: Date = new Date();

    this.state = {
      sensorArray: [],
      typeVariableArray: [],

      date: this.initialDates(),
      flatpickrMode: 'range',
      groupDateTime: '24h',

      dateIni: moment(dIni).add(1, 'days').format('YYYY-MM-DD'),
      timeIni: '00:00',
      dateEnd: moment(dEnd).add(1, 'days').format('YYYY-MM-DD'),
      timeEnd: '00:00',
      groupTime: '10',

      variables: [],
      modalShow: false,
      autoRefresh: true,
    };
  }

  public componentDidMount = (): void => {
    this.mounted = true;
    this.load().catch(() => { /* pass */ }).finally(() => {
      this.submit().catch(() => { /* pass */ });
      this.refresh(true);
    });
  };

  public componentDidUpdate(): void {
    /* pass */
  }

  public componentWillUnmount = (): void => {
    this.mounted = false;
  };

  public initialDates = (): Date[] => {
    const today: Date = new Date();
    const twoDaysBefore: Date = moment().add(-2, 'days').toDate();
    return [twoDaysBefore, today];
  };

  public load = async (): Promise<void> => {
    this.loadingStart();
    try {
      const sensorArray: SensorListDO[] = await sensor<SensorListDO[]>('list', undefined, { typeSensorId: TYPE_SENSOR_ID }) || [];
      const typeVariableArray: TypeSensorVariableListDO[] = await typeSensorVariable<TypeSensorVariableListDO[]>('list', undefined, { typeSensorId: TYPE_SENSOR_ID }) || [];

      const variableNames: string[] = typeVariableArray.map((row: TypeSensorVariableListDO): string => {
        return `${row.variableName} (${row.variableUnit})`;
      });

      if (this.mounted) {
        this.context?.updateParent({
          typeVariableArray,
          data3: variableNames,
        });

        this.setState({
          id: sensorArray[0].id,
          sensorArray,
          typeVariableArray,
          variables: ['1', '2'],
        });
      }
    } catch (error) {
      messageWarning('Warning', 'There are no weather stations in the system');
    }
    this.loadingFinish();
  };

  public hideSidebar = (): void => {
    $('#right-panel').addClass('hidden-functions');
    $('#tab-main-content').removeClass('tab-left-content');
    $('#toggle-func-btn').addClass('transparent sidebar-hidden');
  };

  public submit = async (): Promise<void> => {
    const { id, dateIni, timeIni, dateEnd, timeEnd, groupTime, variables } = this.state;

    const idSensor: number | undefined = id || this.props.rowId;
    if (!idSensor) {
      messageError('Error', 'Please select a device');
      return;
    }

    const ini: string | undefined = (dateIni) ? `${dateIni}${(timeIni) ? `T${timeIni}:00.000Z` : ''}` : undefined;
    const end: string | undefined = (dateEnd) ? `${dateEnd}${(timeEnd) ? `T${timeEnd}:00.000Z` : ''}` : undefined;

    // REQUEST: register_list
    const req1: Record<string, any> = { from: ini, to: end };

    // REQUEST: statistics
    const date: Date = new Date();
    const dIni: string = moment(date).subtract(14, 'days').format('YYYY-MM-DD');
    const dEnd: string = moment(date).add(1, 'days').format('YYYY-MM-DD');

    const req2: Record<string, any> = {
      from: `${dIni}T00:00:00.000Z`,
      to: `${dEnd}T00:00:00.000Z`,
      values: [0, 1, 2, 2, 4],
      operators: ['amean', 'max', 'max', 'min', 'sum'],
      group: 1440,
      round: true,
    };

    // REQUEST: register_graph
    const req4: Record<string, any> = { group: groupTime, values: variables, round: false, from: ini, to: end };

    // SUM aggregation for pluviometry
    const pluvio: string = '4';
    if (variables.includes(pluvio)) {
      const idx: number = variables.indexOf(pluvio);
      req4.operators = Array.from({ length: variables.length }, () => 'mean');
      req4.operators[idx] = 'sum';
    }

    if (!this.isPlottable(req4)) return;

    // REQUEST: windrose
    const req5: Record<string, any> = { dir: 0, speed: 1, from: ini, to: end };

    this.loadingStart();
    try {
      if (this.mounted) {
        if (this.id !== id) {
          const [info, data2] = await Promise.all([
            sensor<SensorDO>('get', String(idSensor)),
            weather('statistics', String(idSensor), req2)
          ]);
          data2.splice(0, 1); // Remove title row of statistics

          this.context?.updateParent({
            nameStation: info?.description,
            data2,
          });
        }

        const [data1, data4T, data5] = await Promise.all([
          sensor('register_list', String(idSensor), req1),
          sensor('register_graph', String(idSensor), req4),
          weather('windrose', String(idSensor), req5),
        ]);

        // Process graph data
        const data4: any = this.processGraphData(data4T);
        this.context?.updateParent({ data1, data4, data5, });
      }
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }

    this.id = id;

    this.loadingFinish();
    this.hideSidebar();
  };

  public toTimestamp = (strDate: string): number => {
    const datum: number = Date.parse(strDate);
    return datum / 1000;
  };

  public processGraphData = (dataSrc: any[]): any => {
    const { typeVariableArray } = this.state;
    const regex: RegExp = /(\d+)/g;

    const dataDst: any[] = dataSrc.map((e: any, i: number) => {
      let { variableName, variableUnit } = typeVariableArray[parseInt(e.name.match(regex), 10)];
      let data: any[] = [];

      // Pressure conversion from [bars] to [hPa] unit
      if (e.name === 'D05') {
        variableName = 'Pressure';
        variableUnit = 'hPa';
        data = e.data.map((point: any) => { return [this.toTimestamp(point[0]) * 1000, point[1] * 1000]; });
      } else {
        data = e.data.map((point: any) => { return [this.toTimestamp(point[0]) * 1000, point[1]]; });
      }

      return {
        name: `${variableName} [${variableUnit}]`,
        type: 'line',
        data,
        yAxis: (i % 2 === 0) ? 0 : 1,
        zIndex: 2,
      };
    });

    return dataDst;
  };

  public isPlottable = (data: any): boolean => {
    const { from, to, group, values } = data;
    if (!from || !to) {
      messageError('Error', 'Please select valid dates for the interval');
      return false;
    }
    if (!group) {
      messageError('Error', 'Please select a time interval');
      return false;
    }
    if (!values || !values.length) {
      messageError('Error', 'Please select at least a variable to plot');
      return false;
    }

    return true;
  };

  public loadingStart = (): void => {
    this.context?.updateParent({
      loading: true,
    });
    window.store.dispatch(initProcess(this.pid));
  };

  public loadingFinish = (): void => {
    this.context?.updateParent({
      loading: false,
    });
    window.store.dispatch(finishProcess(this.pid));
  };

  public handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const { name, value, type, checked } = (e as React.ChangeEvent<HTMLInputElement>).target;
    if (type === 'checkbox') {
      this.setState((prevState: State) => ({
        ...prevState,
        [name]: checked,
      }));
    } else {
      this.setState((prevState: State) => ({
        ...prevState,
        [name]: value,
      }));
    }
  };

  public refresh(create: boolean): void {
    if (create) {
      this.timer = setInterval(() => { this.handleSubmit(); }, 120000);
    } else {
      clearInterval(this.timer);
    }
  }

  public handleAutoRefresh = (autoRefresh: boolean): void => {
    this.setState({
      autoRefresh,
    }, () => { this.refresh(autoRefresh); });
  };

  public handleChangeDevice = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const { name, value } = e.target;
    this.setState((prevState: State) => ({
      ...prevState,
      [name]: value,
    }));
  };

  public handleChangeMultiple = (e: EventSelect2): void => {
    const { id } = e.params.data;
    const { selected } = e.params.data;
    if (selected) {
      this.addValue(id);
    } else {
      this.remValue(id);
    }
  };

  public isValueSelected = (id: string): boolean => {
    const { variables } = this.state || {};
    if (!variables) return false;
    return variables.includes(id);
  };

  public addValue = (id: string): void => {
    if (this.isValueSelected(id)) return;
    const { variables } = this.state || {};
    const selection: any[] = !variables ? [] : variables.slice();
    selection.push(id);
    this.setState({
      variables: selection.sort((a: any, b: any) => {
        return a - b;
      }),
    });
  };

  public remValue = (id: string): void => {
    if (!this.isValueSelected(id)) return;
    const { variables } = this.state || {};
    if (!variables) return;
    const selection: any[] = variables.filter((itemId: any) => itemId !== id);
    this.setState({
      variables: selection,
    });
  };

  public handleSubmit = (): void => {
    this.submit().catch(() => { /* pass */ });
  };

  public handleDatePickerChanges = (config: any, dateKey: string = 'date'): void => {
    const date: Date[] = config.getDates();
    const preDataIni: Date = date[0];
    const newDataIni: string = moment(preDataIni).format('YYYY-MM-DD');
    const preDataEnd: Date = date[1];
    let newDataEnd: string = '';
    if (config.getMode() === 'range') {
      newDataEnd = moment(preDataEnd).format('YYYY-MM-DD');
    } else {
      newDataEnd = moment(newDataIni).add(1, 'days').format('YYYY-MM-DD');
    }

    this.setState((prevState: State) => ({
      ...prevState,
      [dateKey]: date,
      flatpickrMode: config.getMode(),
      dateIni: newDataIni,
      dateEnd: newDataEnd
    }));
  };

  public onModalOpen = (): void => {
    this.setState({
      modalShow: true,
    });
  };

  public onModalClose = (): void => {
    this.setState({
      modalShow: false,
    });
  };

  public render(): JSX.Element {
    const { date, flatpickrMode, groupDateTime } = this.state;
    const { id, modalShow, autoRefresh, sensorArray, typeVariableArray, variables, groupTime } = this.state;

    const user: UserDO = _.get(window.store.getState(), ['user', 'user']);
    const userIsAdmin: boolean = (user.type === 'adm');
    const showMainFilter: boolean = (this.props.rowId == null);

    return (
      <div className="main-filters">
        {showMainFilter && (
          <div className="sideFilter" style={{ marginBottom: '15px' }}>
            <Select2
              name="id"
              data={sensorArray.map((item: SensorListDO): DataSelect2 => {
                return {
                  title: `${item.code} - ${item.name}`,
                  text: `${item.description}`,
                  id: item.id,
                };
              })}
              value={id}
              options={{
                placeholder: 'Select Sensor',
              }}
              onChange={this.handleChangeDevice}
              style={{ width: '100%' }}
            />
          </div>
        )}
        <UiValidate>
          <div className="sideFilter" style={{ marginBottom: '15px' }}>
            <Select2
              multiple
              name="variables"
              data={typeVariableArray.map((item: TypeSensorVariableListDO, idx: number): DataSelect2 => {
                return {
                  title: `${item.variableName} [D${idx}] [${item.variableUnit}]`,
                  text: `${item.variableName}`,
                  id: idx
                };
              })}
              value={variables}
              options={{
                placeholder: 'Select variables',
              }}
              onSelect={this.handleChangeMultiple}
              onUnselect={this.handleChangeMultiple}
              style={{ width: '99%' }}
              required
            />
          </div>
          <DatePicker
            icon
            hours={[]}
            dates={date}
            showChangeModes
            mode={flatpickrMode}
            granularity={groupDateTime}
            iconClass="wdna-ico-icon-calendar"
            handleChanges={this.handleDatePickerChanges}
          />
          <div className="sideFilter" style={{ marginBottom: '15px' }}>
            <div>
              <Select2
                name="groupTime"
                data={[
                  { id: 0, value: 10, text: '10 Minutes' },
                  { id: 1, value: 30, text: '30 Minutes' },
                  { id: 2, value: 60, text: '1 Hour' },
                  { id: 3, value: 1440, text: 'Day' },
                  { id: 4, value: 0, text: 'Raw Data' },
                ]}
                value={groupTime}
                options={{
                  placeholder: 'Select Time Interval',
                }}
                onChange={this.handleChange}
                style={{ width: '100%' }}
                required
              />
            </div>
          </div>
          <div className="auto-refresh-button">
            <br />
            <label htmlFor="normal-switch" style={{ marginRight: '20px' }}>
              <Switch
                id="auto-refresh-button"
                className="react-switch"
                aria-labelledby="neat-label"
                height={30}
                onColor="#78a329"
                offColor="#5c6875"
                offHandleColor="#444"
                onHandleColor="#b7f93d"
                checked={autoRefresh}
                onChange={this.handleAutoRefresh}
              />
              <span style={{ marginLeft: '10px' }}>Auto Refresh</span>
            </label>
          </div>
          <div style={{ marginTop: '1px' }}>
            <button type="button" className="filter-btn btn btn-default" onClick={this.onModalOpen}>
              <i className="wdna-ico-icon-playsearch" />
              <span>History Report</span>
            </button>
          </div>
          <Report id={id} show={modalShow} onClose={this.onModalClose} />
          <div style={{ marginTop: '1px' }}>
            <button type="button" className="filter-btn btn btn-default" onClick={this.handleSubmit}>
              <i className="wdna-ico-icon-playsearch" />
              <span>GO</span>
            </button>
          </div>
        </UiValidate>
      </div>
    );
  }
}
